/**
 * Screen-13 — OO «Дашборд»: приёмка отчётов об играх за день.
 *
 * Mounted into the OO shell's «Дашборд» tab (OoAssignScreen). Implements spec
 * sections A–H: sticky traffic-light summary + progress, branch/status filters
 * (problem cards pinned on top), a feed of one-card-per-game summaries with
 * border-left status, inline click-to-edit price/payment cells (autosave →
 * flash → recompute Δ → edit-history), tea_price=0 banner, accept / return
 * (bottom-sheet with presets) / archive (custom double-confirm) / OO comment,
 * and batch accept/return with a custom confirm.
 *
 * Data + mutations come from useOoReports; all numeric inputs use
 * inputMode="numeric"; overridden values render in --pink; zero raw hex; SVG
 * icons only (no emoji); custom modals/sheet (never window.confirm).
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactElement, type SVGProps } from "react";
import {
  useOoReports, countDay,
  type OoGameSummary, type OoReport, type OoReportEdit, type OoOperatorRow, type PricesPatch,
  type OoMcOp, type OoMcNeed,
} from "../../hooks/useOoReports";
import type { OoDayBrief } from "../../hooks/ooDayBrief";
import { api, apiFileUrl } from "../../api/client";
import { HallAdminTap } from "./HallAdminTap"; import { ReportNudgeChip } from "./ReportNudgeChip"; import { PeerTextsBlock } from "./PeerTextsBlock"; import { DashExtras, OpChip } from "./DashExtras"; import { usePhotoEdit } from "./photoEdit"; import { ExtraReview } from "./ExtraReview"; import { useLongPress } from "./useLongPress"; import { DashHelp } from "./DashHelp"; import { DayBrief } from "./DayBrief"; import { ExtraProposals } from "./ExtraProposals"; import { HallRequestsBlock } from "./HallRequestsBlock"; import { PurchasesBlock } from "./PurchasesBlock"; import { MoveSheet } from "./MoveSheet";
import { GameQrPanel } from "../../components/GameQrPanel";
import { BookingModal } from "../../components/modal";
import { TextPad } from "../../components/shared/TextPad";
import { NumPadPop, PlayersBadge, TimePicker } from "../../components/shared";
import { useExtraWork } from "../../hooks/useExtraWork";
import { dayPayFromSalary, ewPayKey, type DayPay, type SalaryDayResp } from "./dayWorkPay";
import { useAppData } from "../../hooks/useAppData";
import { mcClientTotal } from "@ub/shared";
import { useBookings } from "../../hooks/useBookings";
import type { Booking } from "../../hooks/bookingTypes";
import { fmtDate, fmtDateApi, slotSort, addDays, parseDateApi, todayBusiness, isNightSlot } from "../../utils/date";
import css from "./dashboard.module.css";

/* ── Icons (inline SVG, currentColor, no emoji) ───────────────────────────── */
type P = SVGProps<SVGSVGElement>;
function Ico({ children, ...rest }: P) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" {...rest}>
      {children}
    </svg>
  );
}
const IcCheck = (p: P) => (<Ico {...p}><path d="M3 8.5l3.5 3.5L13 4.5" /></Ico>);
const IcReturn = (p: P) => (<Ico {...p}><path d="M7 4L3 8l4 4" /><path d="M3 8h7a3 3 0 0 1 0 6H8" /></Ico>);
const IcComment = (p: P) => (<Ico {...p}><path d="M2.5 3.5h11v7h-6l-3 2.5V10.5h-2z" /></Ico>);
const IcTrash = (p: P) => (<Ico {...p}><path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 8.5a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.6-8.5" /></Ico>);
const IcWarn = (p: P) => (<Ico {...p}><path d="M8 2l6 11H2L8 2zM8 6.5v3.2M8 11.4v.1" /></Ico>);
const IcPerson = (p: P) => (<Ico {...p}><circle cx="8" cy="5.2" r="2.6" /><path d="M3 13.5v-1a4 4 0 0 1 4-4h2a4 4 0 0 1 4 4v1" /></Ico>);
const IcX = (p: P) => (<Ico {...p}><path d="M4 4l8 8M12 4l-8 8" /></Ico>);
const IcChev = (p: P) => (<Ico {...p}><path d="M4 6l4 4 4-4" /></Ico>);
const IcMore = (p: P) => (<Ico {...p}><circle cx="3.5" cy="8" r="1" fill="currentColor" stroke="none" /><circle cx="8" cy="8" r="1" fill="currentColor" stroke="none" /><circle cx="12.5" cy="8" r="1" fill="currentColor" stroke="none" /></Ico>);
const IcCup = (p: P) => (<Ico {...p}><path d="M3 5h8v4.5a2.5 2.5 0 0 1-2.5 2.5h-3A2.5 2.5 0 0 1 3 9.5V5z" /><path d="M11 6h1.6a1.4 1.4 0 0 1 0 2.8H11" /><path d="M5 2.5v1M7.5 2.5v1" /></Ico>);
const IcCash = (p: P) => (<Ico {...p}><rect x="2" y="4" width="12" height="8" rx="1" /><circle cx="8" cy="8" r="1.8" /></Ico>);
const IcCard = (p: P) => (<Ico {...p}><rect x="2" y="3.5" width="12" height="9" rx="1" /><path d="M2 6.5h12M4.5 10h3" /></Ico>);
const IcTransfer = (p: P) => (<Ico {...p}><path d="M4 5h7l-2-2M12 11H5l2 2" /></Ico>);
const IcWallet = (p: P) => (<Ico {...p}><rect x="2" y="4" width="12" height="8.5" rx="1.5" /><path d="M10.5 8h2.5v1.5h-2.5a0.75 0.75 0 0 1 0-1.5z" /></Ico>);
const IcDoc = (p: P) => (<Ico {...p}><path d="M4 2.5h5l3 3v8H4z" /><path d="M9 2.5v3h3M6 8.5h4M6 10.5h4" /></Ico>);
const IcStar = (p: P) => (<Ico {...p}><path d="M8 2l1.8 3.7L14 6.3l-3 2.9.7 4.1L8 11.4 4.3 13.3 5 9.2 2 6.3l4.2-.6z" /></Ico>);
const IcArrow = (p: P) => (<Ico {...p}><path d="M3 8h9M9 5l3 3-3 3" /></Ico>);
const IcClip = (p: P) => (<Ico {...p}><path d="M12.5 7.2l-5 5a2.4 2.4 0 0 1-3.4-3.4l5.6-5.6a1.7 1.7 0 0 1 2.4 2.4L6.4 11.3" /></Ico>);

/* ── Status filters ───────────────────────────────────────────────────────── */
type StatusFilter = "all" | "disc" | "none" | "corr" | "accepted";
/* Короткие лейблы (24.08, владелец): фильтры помещаются в одну строку */
const STATUS_LABELS: Record<StatusFilter, string> = {
  all: "Все", disc: "Расхожд.", none: "Без отч.", corr: "Дораб.", accepted: "Принято",
};

const RETURN_PRESETS = ["сумма не сходится", "нет чайной", "уточни активность", "проверь предоплату"];

/** Per-game derived status (drives border-left, chips, filters). */
interface GameStatus {
  hasReport: boolean;
  primary: OoReport | undefined;
  diff: number;
  hasDisc: boolean;
  accepted: boolean;
  needsCorr: boolean;
  kind: "none" | "disc" | "corr" | "accepted" | "ok";
}
function deriveStatus(g: OoGameSummary): GameStatus {
  const primary = g.reports.find((r) => r.isPrimary) ?? g.reports[0];
  const hasReport = g.reports.length > 0;
  const diff = primary ? primary.total - primary.expectedTotal : 0;
  const hasDisc = !!primary && Math.abs(diff) >= 1;
  const accepted = !!primary && primary.accepted;
  const needsCorr = !!primary && primary.needsCorrection;
  let kind: GameStatus["kind"];
  if (!hasReport) kind = "none";
  else if (needsCorr) kind = "corr";
  else if (hasDisc && accepted) kind = "accepted";
  else if (hasDisc) kind = "disc";
  else kind = "ok";
  return { hasReport, primary, diff, hasDisc, accepted, needsCorr, kind };
}

const PROBLEM_RANK: Record<GameStatus["kind"], number> = { none: 0, disc: 1, corr: 2, accepted: 3, ok: 4 };

/** Полных лет по ISO-дате рождения (как ageOf в форме оператора). */
function ageFrom(iso: string): number {
  const d = new Date(iso); const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) a--;
  return Math.max(0, a);
}

function fmt(n: number): string {
  return n.toLocaleString("ru-RU");
}

/* ── Day-label «3 июнь ср» style from fmtDate «3 июн · ср» ─────────────────── */
function dayLabel(date: Date): string {
  return fmtDate(date).replace(" · ", " ");
}

/* ── Main view ────────────────────────────────────────────────────────────── */
export function OoDashboardView({ date, onBoardStale }: { date: Date; onBoardStale?: () => void }) {   /* onBoardStale — 11.10: админы залов поправлены из дашборда, доска перечитывается */
  const { games, counts, dayBrief, setDayBrief, loading, error, accept, requestCorrection, savePrices, archive, fetchEdits, reload } =
    useOoReports(date);

  const [branch, setBranch] = useState<string>("all");
  const [status, setStatus] = useState<StatusFilter>("all");
  /* дропдауны фильтров в строке статистики (27.08, владелец); тап мимо — закрыть */
  const [brOpen, setBrOpen] = useState(false);
  const [stOpen, setStOpen] = useState(false);
  const [tipFor, setTipFor] = useState<number | null>(null); const tipT = useRef(0); /* тап по цифре — подсказка */
  /* 28.08 (владелец, v2): ОДНА сумма — за сегодня/прошлое «собрано» (факт
     отчётов), за будущее «прогноз» (ожидание броней). Тап — разбивка по
     способам оплаты из сданных отчётов. */
  const [payOpen, setPayOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false); /* 07.09 (владелец): «?» — объяснитель всего экрана */
  /* 02.09 (владелец): дежурный на DC дня — в «Пришли сегодня» */
  const [dcDuty, setDcDuty] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api<{ dc_duty?: { name: string } | null; dc_duties?: { name: string }[] }>(`/api/assignments/meta?date=${fmtDateApi(date)}`)
      /* 27.09: дежурных DC несколько — через запятую */
      .then((m) => { if (alive) setDcDuty((m.dc_duties ?? (m.dc_duty ? [m.dc_duty] : [])).map((d) => d.name).join(", ") || null); }).catch(() => { if (alive) setDcDuty(null); });
    return () => { alive = false; };
  }, [date]);
  const dayMoney = useMemo(() => {
    const future = fmtDateApi(date) > fmtDateApi(todayBusiness());
    let fact = 0, forecast = 0;
    const pay = { cash: 0, terminal: 0, transfer: 0, prepaid: 0, invoice: 0, change: 0 };
    for (const g of games) {
      const prim = g.reports.find((r) => r.isPrimary) ?? g.reports[0];
      fact += prim ? prim.total : 0;
      forecast += prim ? prim.total : g.expectedRub;
      if (prim) {
        pay.cash += prim.cash; pay.terminal += prim.terminal; pay.transfer += prim.transfer;
        pay.prepaid += prim.prepaidUsed; pay.invoice += prim.invoice; pay.change += prim.changeAmount;
      }
    }
    return { future, sum: future ? forecast : fact, pay };
  }, [games, date]);
  useEffect(() => {
    if (!payOpen) return;
    const onDown = (e: PointerEvent) => { if (!(e.target as HTMLElement | null)?.closest?.(`.${css.payWrap}`)) setPayOpen(false); };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [payOpen]);
  useEffect(() => {
    if (!brOpen && !stOpen) return;
    const onDown = (e: PointerEvent) => { if (!(e.target as HTMLElement | null)?.closest?.(`.${css.fWrap}`)) { setBrOpen(false); setStOpen(false); } };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [brOpen, stOpen]);
  const [batch, setBatch] = useState<Set<string>>(new Set());
  const [qrBookingId, setQrBookingId] = useState<number | null>(null);

  // Modal / sheet host state (lifted so they overlay the whole screen).
  const [returnTarget, setReturnTarget] = useState<{ game: OoGameSummary; reportId: number } | null>(null);
  /* 28.08 (владелец, v2): лонг-пресс по времени — ПОЛНОЦЕННАЯ модалка изменения
     брони (та же, что в сетке ДК): правка, отмена с причиной, вся механика.
     useBookings сам тянет день+ночной хвост — ночные игры тоже открываются. */
  const [bmBookingId, setBmBookingId] = useState<number | null>(null);
  /* 28.08 (владелец): перенос из дашборда — свой пикер (move-режима сетки тут нет) */
  const [moveTarget, setMoveTarget] = useState<Booking | null>(null);
  const [bmToast, setBmToast] = useState<string | null>(null);
  useEffect(() => { if (!bmToast) return; const t = window.setTimeout(() => setBmToast(null), 2500); return () => window.clearTimeout(t); }, [bmToast]);
  const app = useAppData();
  /* 03.09 (владелец): из «Пришли сегодня» открывается оригинальная модалка брони —
     бронь создана сегодня, но игра может быть на другую дату: грузим её день */
  const [bmDate, setBmDate] = useState<string | null>(null);
  const modalDate = useMemo(() => (bmDate ? parseDateApi(bmDate) : date), [bmDate, date]); /* стабильный объект — иначе useBookings перезапрашивает на каждый рендер */
  const bk = useBookings(modalDate);
  /* 03.09: бронь по id не нашлась в дне (ночной слот, отменена, ошибка сети) — иначе bmDate
     «залипал» на чужом дне и long-press по отчёту открывал пустоту */
  /* resolvedRef: бронь уже показывалась в модалке — если потом пропала из дня (перенесли из
     модалки, отменил другой сотрудник), закрываем ТИХО, не перебивая тост «перенесена».
     moveTargetRef: пока открыт MoveSheet, день модалки не сбрасываем — ему нужны busyKeys того дня. */
  const resolvedRef = useRef(false);
  const moveTargetRef = useRef<Booking | null>(null);
  useEffect(() => {
    if (bmBookingId == null || bk.loading) return;
    /* ошибка загрузки — терминал: карта чужого дня не придёт */
    if (bk.error) { setBmBookingId(null); setBmDate(null); setBmToast("Не удалось загрузить бронь"); return; }
    /* проверяем только карту НУЖНОГО дня: при смене modalDate bookings ещё вчерашние, а loading уже false */
    if (bk.loadedFor !== fmtDateApi(modalDate)) return;
    const found = Object.values(bk.bookings).some((sd) => sd.type !== "auto-block" && (sd as Booking).id === bmBookingId);
    if (found) { resolvedRef.current = true; return; }
    setBmBookingId(null); if (!moveTargetRef.current) setBmDate(null);
    if (!resolvedRef.current) setBmToast("Бронь не найдена в сетке дня");
  }, [bmBookingId, bk.loading, bk.error, bk.bookings, bk.loadedFor, modalDate]);
  const [archiveTarget, setArchiveTarget] = useState<OoGameSummary | null>(null);
  const [batchConfirm, setBatchConfirm] = useState<null | { kind: "accept" | "return"; n: number; sum: number }>(null);

  // Reset batch selection whenever the day changes / data reloads.
  useEffect(() => { setBatch(new Set()); }, [date]);

  const statuses = useMemo(() => new Map(games.map((g) => [g.id, deriveStatus(g)])), [games]);

  const branches = useMemo(() => {
    const set = new Set<string>();
    games.forEach((g) => g.branchId && set.add(g.branchId));
    /* 02.09: пустой день — филиалы из справочника, чтобы дропдаун не пропадал */
    if (!set.size) app.branches.forEach((b) => b.active !== 0 && set.add(b.id));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "ru"));
  }, [games, app.branches]);

  // Status-filter counts (for the chip badges).
  const filterCounts = useMemo(() => {
    const base = branch === "all" ? games : games.filter((g) => g.branchId === branch);
    const c = countDay(base);
    const accepted = base.filter((g) => statuses.get(g.id)?.kind === "accepted").length;
    return { all: base.length, disc: c.disc, none: c.none, corr: c.corr, accepted };
  }, [games, branch, statuses]);

  const visible = useMemo(() => {
    let list = branch === "all" ? games : games.filter((g) => g.branchId === branch);
    if (status !== "all") {
      list = list.filter((g) => {
        const st = statuses.get(g.id);
        if (!st) return false;
        if (status === "disc") return st.kind === "disc";
        if (status === "none") return st.kind === "none";
        if (status === "corr") return st.kind === "corr";
        if (status === "accepted") return st.kind === "accepted";
        return true;
      });
    }
    /* 12.09 (владелец): порядок ленты не меняется НИКОГДА. Раньше карточки стояли по
       «проблемности», и отправка на коррекцию перекидывала карточку в другую группу —
       ОО терял место, с которого разбирал день. Порядок — по времени игры, а проблемные
       и так видно по рамке и меткам. */
    return [...list].sort((a, b) => a.time.localeCompare(b.time) || a.id.localeCompare(b.id));
  }, [games, branch, status]);

  const toggleBatch = useCallback((id: string) => {
    setBatch((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);

  // Batch metrics: only discrepancy (!accepted) cards are eligible (never «нет отчёта»).
  const batchGames = useMemo(
    () => [...batch].map((id) => games.find((g) => g.id === id)).filter((g): g is OoGameSummary => !!g),
    [batch, games],
  );
  const batchSum = useMemo(
    () => batchGames.reduce((acc, g) => acc + Math.abs(statuses.get(g.id)?.diff ?? 0), 0),
    [batchGames, statuses],
  );

  const runBatchAccept = useCallback(async () => {
    for (const g of batchGames) {
      const st = statuses.get(g.id);
      if (st?.primary) await accept(st.primary.id);
    }
    setBatch(new Set());
    setBatchConfirm(null);
  }, [batchGames, statuses, accept]);

  // Loading skeletons.
  if (loading) {
    return (
      <div className={css.root}>
        <div className={css.scroll}>
          <div className={css.summary}>
            <div className={css.traffic}><span className={css.tseg}><span className={css.segTotal}>Загрузка дня…</span></span></div>
            <div className={css.prog}><span className={css.progFill} style={{ width: "12%" }} /></div>
          </div>
          <div className={css.content}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={css.skel}>
                <div className={css.skelLine} style={{ width: "62%" }} />
                <div className={css.skelLine} style={{ width: "90%" }} />
                <div className={css.skelLine} style={{ width: "44%" }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const showBulk = batch.size > 0;
  /* 28.08 (владелец): ВСЕ карточки дня зелёные — день «ещё зеленее», пульс границ */
  const allClean = counts.total > 0 && counts.none === 0 && counts.disc === 0 && counts.corr === 0;

  return (
    <div className={css.root}>
      <div className={css.scroll}>
      {/* A. Sticky summary — ОДНА строка: статистика + дропдауны филиала и фильтра (27.08, владелец) */}
      <div className={css.summary}>
        <div className={css.traffic}>
          <span className={`${css.tseg} ${css.segTotal}`}><span className={css.n}>{counts.total}</span> {gamesWord(counts.total)}</span>
          {/* 27.08 (владелец): только ЦИФРЫ — слово всплывает по тапу подсказкой */}
          {([
            [counts.done, css.segDone, "сдано"] as const,
            [counts.disc, css.segDisc, "расхождения"] as const,
            [counts.none, css.segNone, "без отчёта"] as const,
            [counts.corr, css.segCorr, "на доработке"] as const,
          ]).map(([n, cls, label], i) =>
            n > 0 ? (
              <Fragment key={i}>
                <span className={css.tsep}>·</span>
                <span className={css.tsegWrap}>
                  <button type="button" className={`${css.tseg} ${css.tsegBtn} ${cls}`} title={label}
                    onClick={() => { setTipFor((t) => (t === i ? null : i)); window.clearTimeout(tipT.current); tipT.current = window.setTimeout(() => setTipFor(null), 1800); }}>
                    <span className={css.n}>{n}</span>
                  </button>
                  {tipFor === i ? <span className={css.tsegTip}>{label}</span> : null}
                </span>
              </Fragment>
            ) : null,
          )}
          {/* 28.08 (владелец): ОДНА сумма дня; тап — разбивка по способам оплаты.
              02.09: показываем и в пустой день (₽0) — нарезка экрана не меняется */}
          {true ? (
            <span className={`${css.tsegWrap} ${css.payWrap}`}>
              <button type="button" className={`${css.tseg} ${css.tsegBtn} ${css.segMoney}`}
                title={dayMoney.future ? "прогноз за день — тап: способы оплаты" : "собрано за день — тап: способы оплаты"}
                onClick={() => setPayOpen((v) => !v)}>
                <span className={css.n}>{dayMoney.future ? "~" : ""}₽{fmt(dayMoney.sum)}</span>
              </button>
              {payOpen ? (
                <span className={css.payList}>
                  <span className={css.payHead}>{dayMoney.future ? "Прогноз за день" : "Собрано за день"}</span>
                  {([["Нал", dayMoney.pay.cash], ["Терминал", dayMoney.pay.terminal], ["Перевод", dayMoney.pay.transfer],
                     ["ЗДТК", dayMoney.pay.prepaid], ["Р/С", dayMoney.pay.invoice]] as [string, number][])
                    .filter(([, v]) => v > 0)
                    .map(([l, v]) => <span key={l} className={css.payRow}><span>{l}</span><b>₽{fmt(v)}</b></span>)}
                  {dayMoney.pay.change > 0 ? <span className={css.payRow}><span>Сдача</span><b className={css.payMinus}>−₽{fmt(dayMoney.pay.change)}</b></span> : null}
                  {dayMoney.pay.cash + dayMoney.pay.terminal + dayMoney.pay.transfer + dayMoney.pay.prepaid + dayMoney.pay.invoice === 0
                    ? <span className={css.payRow}><span>отчётов с оплатой ещё нет</span></span> : null}
                </span>
              ) : null}
            </span>
          ) : null}
          {true ? (
            <span className={css.fDrops}>
              {branches.length > 1 ? (
                <span className={css.fWrap}>
                  <button type="button" className={`${css.fBtn} ${branch !== "all" ? css.on : ""}`} onClick={() => { setBrOpen((v) => !v); setStOpen(false); }}>
                    {branch === "all" ? "Филиал" : branch} ▾
                  </button>
                  {brOpen ? (
                    <span className={css.fList}>
                      <button type="button" className={css.fItem} onClick={() => { setBranch("all"); setBrOpen(false); }}>Все</button>
                      {branches.map((b) => (
                        <button key={b} type="button" className={css.fItem} onClick={() => { setBranch(b); setBrOpen(false); }}>{b}</button>
                      ))}
                    </span>
                  ) : null}
                </span>
              ) : null}
              <span className={css.fWrap}>
                <button type="button" className={`${css.fBtn} ${status !== "all" ? css.on : ""}`} onClick={() => { setStOpen((v) => !v); setBrOpen(false); }}>
                  {STATUS_LABELS[status]} ▾
                </button>
                {stOpen ? (
                  <span className={css.fList}>
                    {(Object.keys(STATUS_LABELS) as StatusFilter[]).map((s) => {
                      const cnt = s === "all" ? filterCounts.all : filterCounts[s];
                      return (
                        <button key={s} type="button" className={css.fItem} onClick={() => { setStatus(s); setStOpen(false); }}>
                          {STATUS_LABELS[s]}{s !== "all" && cnt > 0 ? ` · ${cnt}` : ""}
                        </button>
                      );
                    })}
                  </span>
                ) : null}
              </span>
              {/* 07.09 (владелец): «?» — исчерпывающий объяснитель кнопок, цифр, рамок и цветов экрана */}
              <button type="button" className={`${css.fBtn} ${css.helpBtn} ${helpOpen ? css.on : ""}`} title="Что здесь что значит" aria-label="Справка по экрану"
                onClick={() => { setHelpOpen((o) => !o); setBrOpen(false); setStOpen(false); }}>?</button>
            </span>
          ) : null}
        </div>
        <div className={css.prog}>
          <span className={css.progFill} style={{ width: `${counts.total ? Math.round((counts.done / counts.total) * 100) : 0}%` }} />
        </div>
        {helpOpen ? <DashHelp onClose={() => setHelpOpen(false)} /> : null}
      </div>

      <ExtraProposals date={date} /> {/* заявки операторов на допработы (26.08, владелец); 11.10 — только за эту дату */} <HallRequestsBlock date={date} /> {/* 28.09: заявки админов залов на правки расстановки */}
      <DayBrief brief={dayBrief} games={games} dcDuty={dcDuty} onOpenBooking={(id, d) => { resolvedRef.current = false; setBmDate(d); setBmBookingId(id); }} /> {/* «Пришли сегодня» + «Сегодня работают» (24.08, владелец) */}
      <DayWork date={date} halladmins={dayBrief.halladmins} onHallAdmins={(h) => { if (h) setDayBrief((b) => ({ ...b, halladmins: h })); onBoardStale?.(); }} /> {/* 28.08: допработы и админства с правкой ставок; 11.10: тап по админу — второй/заменить/снять */} <PurchasesBlock date={date} /> {/* 04.09 (владелец): покупки операторов на подтверждение, лайм */}

      {/* B. Feed / empty states */}
      {games.length === 0 && error ? (
        /* 02.09: упавший фетч — честная ошибка, а не выдуманные игры */
        <div className={css.emptyState}>
          <span className={css.esIco}><Ico width={40} height={40}><circle cx="8" cy="8" r="6" /><path d="M8 5v3.5M8 11v.1" /></Ico></span>
          <span className={css.esT}>Не удалось загрузить день</span>
          <span className={css.esS}>{error}</span>
          <button type="button" className={css.esBtn} onClick={reload}>Повторить</button>
        </div>
      ) : games.length === 0 ? (
        <div className={css.emptyState}>
          <span className={css.esIco}><Ico width={40} height={40}><circle cx="8" cy="8" r="6" /><path d="M8 5v3.5M8 11v.1" /></Ico></span>
          <span className={css.esT}>На {dayLabel(date)} игр нет</span>
          <span className={css.esS}>Выберите другую дату.</span>
        </div>
      ) : visible.length === 0 ? (
        <div className={css.emptyState}>
          <span className={css.esIco}><Ico width={40} height={40}><path d="M2.5 3h11l-4 5v4l-3 1.5V8z" /></Ico></span>
          <span className={css.esT}>Под фильтр ничего не попало</span>
          <button type="button" className={css.esBtn} onClick={() => { setStatus("all"); setBranch("all"); }}>Сбросить</button>
        </div>
      ) : (
        <div className={`${css.content} ${showBulk ? css.contentBulk : ""} ${allClean ? css.allClean : ""}`}>
          {visible.map((g) => (
            <ReportCard
              key={g.id}
              game={g}
              st={statuses.get(g.id)!}
              batchOn={batch.has(g.id)}
              batchMode={showBulk}
              onToggleBatch={() => toggleBatch(g.id)}
              onAccept={(rid) => accept(rid)}
              onReturn={(rid) => setReturnTarget({ game: g, reportId: rid })}
              onArchive={() => setArchiveTarget(g)}
              onSavePrices={savePrices}
              /* 12.09 (владелец): «не нужно перезагружать страницу» — ОО разбирает ленту
                 сверху вниз, и общий reload отматывал её в начало. Карточка гасит баннер
                 у себя, список не трогаем. */
              onNoTea={async (bookingId) => { await api(`/api/bookings/${bookingId}`, { method: "PUT", body: { tea: false } }).catch(() => {}); }}
              fetchEdits={fetchEdits}
              onQr={(bookingId) => setQrBookingId(bookingId)} onRateSaved={reload}
              onCancelGame={(gg) => { resolvedRef.current = false; setBmDate(null); setBmBookingId(gg.bookingId); }}
            />
          ))}
        </div>
      )}
      </div>

      {/* E. Bulk bar */}
      {showBulk ? (
        <div className={css.bulkBar}>
          <div className={css.bbInfo}><span className={css.n}>{batch.size}</span> выбрано · {fmt(batchSum)} ₽</div>
          <button type="button" className={`${css.bbBtn} ${css.bbReturn}`} onClick={() => setBatchConfirm({ kind: "return", n: batch.size, sum: batchSum })}>Вернуть {batch.size}</button>
          <button type="button" className={`${css.bbBtn} ${css.bbAccept}`} onClick={() => setBatchConfirm({ kind: "accept", n: batch.size, sum: batchSum })}>Принять {batch.size}</button>
        </div>
      ) : null}

      {/* D. Return-reason sheet */}
      {returnTarget ? (
        <ReturnSheet
          game={returnTarget.game}
          onClose={() => setReturnTarget(null)}
          onSubmit={async (reason, opIds) => {
            await requestCorrection(returnTarget.reportId, reason, opIds);
            setReturnTarget(null);
          }}
        />
      ) : null}

      {bmBookingId != null ? (() => {
        const entry = Object.entries(bk.bookings).find(([, sd]) => sd.type !== "auto-block" && (sd as Booking).id === bmBookingId);
        if (!entry) return null; /* бронирования дня ещё грузятся */
        const b = entry[1] as Booking;
        return (
          <BookingModal
            questId={b.questId}
            time={b.time}
            curDate={modalDate}
            booking={b}
            conflictLead={null}
            dayBookings={bk.bookings}
            questSlots={app.slots.filter((sl) => sl.quest_id === b.questId && sl.active).map((sl) => sl.time).sort(slotSort)}
            onSave={async (payload, d) => { await bk.save(payload, d); reload(); }}
            onClose={() => { setBmBookingId(null); if (!moveTargetRef.current) setBmDate(null); }}
            onToast={setBmToast}
            onReload={() => { bk.reload(); reload(); }}
            onStartMove={(b2) => { moveTargetRef.current = b2; setMoveTarget(b2); }}
          />
        );
      })() : null}
      {bmToast ? <div className={css.bmToast}>{bmToast}</div> : null}
      {moveTarget ? (
        <MoveSheet
          b={moveTarget}
          gridDate={modalDate} /* 03.09: модалка может быть открыта на бронь другого дня */
          onClose={() => { moveTargetRef.current = null; setMoveTarget(null); setBmDate(null); }}
          onDone={() => { moveTargetRef.current = null; setMoveTarget(null); setBmDate(null); bk.reload(); reload(); setBmToast("Бронь перенесена"); }}
          onMove={bk.move}
          busyKeys={new Set(Object.entries(bk.bookings).filter(([, sd]) => sd.type !== "auto-block" && (sd as Booking).id !== moveTarget.id).map(([k]) => k))}
        />
      ) : null}

      {/* D. Archive double-confirm */}
      {archiveTarget ? (
        <ConfirmModal
          kind="archive"
          title="Архивировать карточку"
          body={<>Карточка <b>{archiveTarget.time} {archiveTarget.questShort}</b> и все её отчёты будут отправлены в архив и исчезнут из расписания. Действие каскадное.</>}
          confirmLabel="В архив"
          onCancel={() => setArchiveTarget(null)}
          onConfirm={async () => { await archive(archiveTarget.bookingId); setArchiveTarget(null); }}
        />
      ) : null}

      {/* E. Batch confirm */}
      {batchConfirm ? (
        <ConfirmModal
          kind={batchConfirm.kind === "accept" ? "accept" : "archive"}
          title={batchConfirm.kind === "accept" ? "Принять расхождения" : "Вернуть на коррекцию"}
          body={
            batchConfirm.kind === "accept"
              ? <>Принять <b>{batchConfirm.n}</b> {discWord(batchConfirm.n)} на сумму <b>{fmt(batchConfirm.sum)} ₽</b>?</>
              : <>Вернуть <b>{batchConfirm.n}</b> {discWord(batchConfirm.n)} на коррекцию? Причину укажете по каждой отдельно.</>
          }
          confirmLabel={batchConfirm.kind === "accept" ? "Принять" : "Понятно"}
          singleStep
          onCancel={() => setBatchConfirm(null)}
          onConfirm={async () => {
            if (batchConfirm.kind === "accept") await runBatchAccept();
            else { setBatchConfirm(null); setBatch(new Set()); }
          }}
        />
      ) : null}
      {qrBookingId != null ? <GameQrPanel bookingId={qrBookingId} onClose={() => setQrBookingId(null)} /> : null}
    </div>
  );
}

/* ── B. One game = one summary card ───────────────────────────────────────── */
function ReportCard({
  game, st, batchOn, batchMode,
  onToggleBatch, onAccept, onReturn, onArchive, onSavePrices, onNoTea, fetchEdits, onQr, onRateSaved, onCancelGame,
}: {
  game: OoGameSummary;
  st: GameStatus;
  batchOn: boolean;
  batchMode: boolean;
  onToggleBatch: () => void;
  onAccept: (reportId: number) => void;
  onReturn: (reportId: number) => void;
  onArchive: () => void;
  onSavePrices: (reportId: number, patch: PricesPatch) => Promise<void>;
  onNoTea: (bookingId: number) => Promise<void> | void;
  fetchEdits: (bookingId: number) => Promise<OoReportEdit[]>;
  onQr: (bookingId: number) => void; onRateSaved: () => void; onCancelGame: (game: OoGameSummary) => void;
}) {
  const tlp = useRef(0); const tlpFired = useRef(false); /* лонг-пресс по времени — отмена */
  const { primary, hasReport, hasDisc, accepted, needsCorr, kind } = st;
  /* bump после каждой правки цены в FinBlock → Expanded перечитывает историю */
  const [editsKey, setEditsKey] = useState(0);

  /* 27.08 (владелец): МирКв — стандартная статусная рамка + тонкая белая ВНУТРИ,
     а не вместо неё */
  const stCls =
    kind === "none" ? css.stNone :
    kind === "disc" ? css.stDisc :
    kind === "corr" ? css.stCorr :
    kind === "accepted" ? css.stAccepted :
    css.stOk;

  // Operator chips: report present (by name or id) → ✓ green, else ✗ red.
  const reportedNames = useMemo(
    () => new Set(game.reports.flatMap((r) => [r.operatorName, r.operatorId])),
    [game.reports],
  );
  const missingOps = game.cancelled ? [] : game.operators.filter((op) => !reportedNames.has(op)); /* 06.09: по отменённой игре отчёт не ждём */

  // Batch checkbox is only available for accept-eligible discrepancy cards.
  const batchEligible = kind === "disc";

  const statusChip = !hasReport
    ? (game.cancelled ? null : <ReportNudgeChip bookingId={game.bookingId} label="Нет" className={`${css.rcStatuschip} ${css.chipNone}`} missing={missingOps} />) /* 06.09: у отменённой — только «ОТМЕНЕНА»; 13.10: тап — «напомнить не сдавшим» */
    : missingOps.length > 0
      ? <ReportNudgeChip bookingId={game.bookingId} label={`${game.reports.length} отч.`} className={`${css.rcStatuschip} ${css.chipSome}`} missing={missingOps} />
      : <span className={`${css.rcStatuschip} ${css.chipDone}`}>Все сданы</span>;

  return (
    <article className={`${css.rcard} ${stCls} ${game.isMirKv ? css.mirkv : ""} ${batchOn ? css.selBatch : ""}`}>
      <div className={css.rcHead}>
        {batchMode && batchEligible ? (
          <button type="button" className={`${css.rcCb} ${batchOn ? css.on : ""}`} aria-label="Выбрать" aria-pressed={batchOn} onClick={onToggleBatch}>
            <IcCheck />
          </button>
        ) : null}
        {/* 28.08 (владелец): лонг-пресс по таймслоту — отмена игры (игроки не пришли) */}
        <span className={css.rcTime}
          onPointerDown={(e) => { e.stopPropagation(); tlpFired.current = false; tlp.current = window.setTimeout(() => { tlpFired.current = true; onCancelGame(game); }, 450); }}
          onPointerUp={() => window.clearTimeout(tlp.current)} onPointerLeave={() => window.clearTimeout(tlp.current)}
          onContextMenu={(e) => e.preventDefault()}
        >{game.time}</span>
        <span className={css.rcQuest}>{game.questShort}</span>
        {game.cancelled ? <span className={css.rcCancelled} title={game.cancelReason || "Игра отменена"}>ОТМЕНЕНА</span> : null}
        {/* 05.09 (владелец): стандартный бейдж игроков — сам открывает карточку клиента.
            08.09: в агрегированных отчётах ОО — расширенная форма «по брони → фактически из отчёта» */}
        <PlayersBadge count={game.players} actual={game.actualPlayers} bookingId={game.bookingId} size="md" />
        <button type="button" className={css.qrBtn} onClick={(e) => { e.stopPropagation(); onQr(game.bookingId); }}>QR</button>
        {/* МирКв: только символ (23.08, Саша) — ★5.0 / красный ✕ (отмена) / серый ✕ (исход не указан) */}
        {game.isMirKv ? (
          game.mkStatus === "score" ? <span className={`${css.badgeMk} ${css.mkScore}`} title="Мир Квестов: оценки"><IcStar />5.0</span>
          : game.mkStatus === "cancel" ? <span className={`${css.badgeMk} ${css.mkCancel}`} title="Мир Квестов: отменили">✕</span>
          : <span className={`${css.badgeMk} ${css.mkNone}`} title={hasReport ? "Мир Квестов: исход не указан в отчёте" : "Мир Квестов: отчёта ещё нет"}>✕</span>
        ) : null}
        {hasDisc && !accepted ? <span className={css.badgeDisc} title="Расхождение"><IcWarn /></span> : null}
        {statusChip}
      </div>

      {/* 27.08 (владелец): дубль денег убран — сверху сразу «Услуги | Оплата»
          (перенесён из разбора, цены кликабельны) */}
      {primary ? (
        <FinBlock game={game} primary={primary} onSavePrices={onSavePrices} onNoTea={onNoTea} onPersisted={() => setEditsKey((k) => k + 1)} />
      ) : null}

      <div className={css.rcOps}>
        {game.operatorRows.length > 0
          /* 27.08 (владелец): первичный — первым; такси правится из чипа */
          ? [...game.operatorRows].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary)).map((row) => (
              <OpChip key={row.operatorId} row={row} questId={game.questId} isMirKv={game.isMirKv} onSaved={onRateSaved}
                /* 28.08: тихий PUT без reload — чип обновляет себя сам */
                onTaxi={row.reportId != null ? async (v) => { await api(`/api/game-reports/${row.reportId}/prices`, { method: "PUT", body: { taxi_amount: v } }); } : undefined} />
            ))
          : game.operators.map((op) => {
              const ok = reportedNames.has(op);
              return (
                <span key={op} className={`${css.opchip} ${ok ? css.chipOk : css.chipWarn}`}>
                  <span className={css.opName}>{op}</span>
                </span>
              );
            })}
        {/* 28.08 (владелец): допуслуги/МК — кто вёл и ставка, правится как у операторов */}
        {game.mcOps.map((m) => <McOpChip key={m.id} m={m} />)}
        {/* 19.09 (владелец): услуга добавлена в бронь после игры — оператор на неё не назначен;
            в сводке её раньше не было вовсе, и МК никому не оплачивался */}
        {game.mcNeed.map((n) => (
          <McNeedChip key={n.mcId} need={n} bookingId={game.bookingId}
            ops={game.operatorRows.map((r) => ({ id: r.operatorId, name: r.name }))} onAssigned={onRateSaved} />
        ))}
      </div>

      <DashExtras bookingId={game.bookingId} materials={game.materials} unifans={game.unifans} uploader={<MaterialsUploader bookingId={game.bookingId} />} />
      {game.dcComment ? (
        <div className={`${css.rcLine} ${css.lineDc}`}><IcComment /><span><span className={css.nm}>ДК</span>{game.dcComment}</span></div>
      ) : null}
      {game.reports.map((r) => r.comment ? (
        <div key={`c${r.id}`} className={`${css.rcLine} ${css.lineOp}`}><IcComment /><span><span className={css.nm}>{r.operatorName}</span>{r.comment}</span></div>
      ) : null)}

      {missingOps.length > 0 ? (
        <div className={`${css.rcLine} ${css.lineMiss}`}><IcWarn /><span>Нет отчёта: {missingOps.join(", ")}</span></div>
      ) : null}
      {needsCorr && primary?.correctionReason ? (
        <div className={`${css.rcLine} ${css.lineCorr}`}><IcReturn /><span>Коррекция: {primary.correctionReason}</span></div>
      ) : null}

      {/* 27.08 (владелец): без «Подробнее» и «…» — карточка всегда развёрнута,
          вся информация об игре видна сразу; действия — в футере разбора */}
      {primary ? (
        <Expanded
          game={game}
          primary={primary}
          st={st}
          onAccept={() => onAccept(primary.id)}
          onReturn={() => onReturn(primary.id)}
          onArchive={onArchive}
          onSavePrices={onSavePrices}
          fetchEdits={fetchEdits}
          editsKey={editsKey}
        />
      ) : <PeerTextsBlock game={game.cancelled ? { ...game, peers: [] } : game} person={<IcPerson />} arrow={<IcArrow />} />}

    </article>
  );
}

/* ── МК/допуслуга игры (28.08, владелец): чип цвета МК (--mc, 11.10) «🎓 услуга · кто вёл»
   со ставкой, редактируемой как у операторов (PUT /assignments/mc-op/:id),
   обновление локальное — без перезагрузки. ───────────────────────────────── */
/* 19.09 (владелец): «во время игры выяснилось, что есть мастер-класс» — ДК дописывает услугу
   в бронь, но оператора на неё никто не назначал, поэтому в сводке её не было и МК не попадал
   в зарплату. Чип показывает такую услугу и в один тап назначает оператора этой игры. */
function McNeedChip(
  { need, bookingId, ops, onAssigned }:
  { need: OoMcNeed; bookingId: number; ops: { id: string; name: string }[]; onAssigned?: () => void },
) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const assign = async (operatorId: string) => {
    setBusy(true); setErr("");
    try {
      await api("/api/mc-booking-ops", { method: "POST", body: { booking_id: bookingId, mc_id: need.mcId, operator_id: operatorId } });
      setOpen(false);
      onAssigned?.();
    } catch (e) { setErr(e instanceof Error ? e.message : "Не удалось назначить"); } finally { setBusy(false); }
  };
  return (
    <span className={css.opPopWrap}>
      <button type="button" className={`${css.opchip} ${css.chipWarn}`} onClick={() => setOpen((v) => !v)} disabled={busy}>
        <span className={css.opName}>🎓 {need.mcName} · кто вёл?</span>
      </button>
      {open ? (
        <div className={css.morePop}>
          {ops.length === 0 ? <div className={css.rateErr}>На игре нет операторов — назначьте в расстановке</div> : null}
          {ops.map((o) => (
            <button key={o.id} type="button" className={css.mpItem} disabled={busy} onClick={() => void assign(o.id)}>{o.name}</button>
          ))}
          {err ? <div className={css.rateErr}>{err}</div> : null}
        </div>
      ) : null}
    </span>
  );
}

function McOpChip({ m }: { m: OoMcOp }) {
  const [rate, setRate] = useState(m.rate);
  const [padOpen, setPadOpen] = useState(false);
  const [err, setErr] = useState("");
  const ref = useRef<HTMLSpanElement | null>(null);
  const save = async (v: number) => {
    if (!(v >= 0)) return;
    try { await api(`/api/assignments/mc-op/${m.id}`, { method: "PUT", body: { rate_paid: v } }); setRate(v); setErr(""); }
    catch (e) { setErr(e instanceof Error ? e.message : "Ошибка"); }
  };
  return (
    <span className={css.opPopWrap}>
      <button type="button" className={`${css.opchip} ${css.chipMc}`} onClick={() => setPadOpen((v) => !v)}>
        <span className={css.opName}>🎓 {m.mcName} · {m.opName}</span>
        <span ref={ref} className={css.opRate} title="Тап — изменить ставку">₽{String(Math.round(rate)).replace(/\B(?=(\d{3})+(?!\d))/g, " ")}</span>
      </button>
      <NumPadPop
        open={padOpen} anchor={ref.current} label={`Ставка МК · ${m.opName}`}
        mode="money" value={rate} max={99999}
        /* 02.09 (владелец): галочка = «Записать» — раньше галочка ничего не делала */
        onCommit={(v) => void save(v)} onClose={() => setPadOpen(false)}
        extraKeys={[{ label: "Записать", onPress: (v) => { setPadOpen(false); void save(v); } }]}
      />
      {err ? <div className={css.morePop}><div className={css.rateErr}>{err}</div></div> : null}
    </span>
  );
}

/* ── Допработы и админства дня (28.08, владелец): строками с деньгами; тап по ₽ — NumPad: допработа → PUT /extra-work/:id {rate_override}, адм зала → PUT /finance/ha-rate.
   10.10: «за период ›» и долгое нажатие по строке — окно «Проверка допработ за период» (ExtraReview), неделя по этот день. ── */
function DayWork({ date, halladmins, onHallAdmins }: { date: Date; halladmins: OoDayBrief["halladmins"]; onHallAdmins?: (h: OoDayBrief["halladmins"] | null) => void }) {
  const extra = useExtraWork(date);
  /* суммы берём из зарплатного расчёта дня — ровно те же числа, что в HR:
     у допработ вычитается пересечение с играми, у админства учитывается
     отчёт смены и пер-дневный оверрайд. */
  const [sal, setSal] = useState<DayPay>({ ew: new Map(), ha: new Map() });
  const [haRates, setHaRates] = useState<Record<string, number>>({});
  const [haDefault, setHaDefault] = useState(0);
  const [pad, setPad] = useState<null | { anchor: HTMLElement; label: string; value: number; save: (v: number) => Promise<void> }>(null);
  /* 12.09 (владелец): ОО задаёт итоговое отработанное время прямо в сводке дня. */
  const [timePick, setTimePick] = useState<null | { label: string; value: string; save: (from: string, to: string) => Promise<void> }>(null);
  /* 12.09 (владелец): тап по ключу раскрывает подробности работы — полный комментарий
     целиком и действия: вернуть оператору на исправление или убрать совсем. */
  const [openEw, setOpenEw] = useState<number | null>(null);
  const [delArm, setDelArm] = useState<number | null>(null);
  const [tick, setTick] = useState(0); const [review, setReview] = useState(false); const hold = useLongPress(() => setReview(true)); const [haInfo, setHaInfo] = useState<string | null>(null); const haTimer = useRef(0); useEffect(() => () => window.clearTimeout(haTimer.current), []);
  const d = fmtDateApi(date);

  const loadSalary = useCallback(() => {
    /* 03.10: сумма допработы — по исполнителю (такси-строка плательщика затирала её нулём), см. dayWorkPay.ts */
    api<SalaryDayResp>(`/api/finance/salary?from=${d}&to=${d}`)
      .then((r) => setSal(dayPayFromSalary(r, d))).catch(() => {});
  }, [d]);
  useEffect(() => { loadSalary(); }, [loadSalary, tick]);
  useEffect(() => {
    let alive = true;
    api<{ results?: { key?: string; value?: string }[] }>("/api/finance/config")
      .then((r) => {
        if (!alive) return;
        const m: Record<string, number> = {}; let def = 0;
        for (const row of r.results ?? []) {
          const k = String(row.key ?? "");
          if (k === "hall_admin_hourly_rate") def = Number(row.value) || 0;
          else if (k.startsWith("ha_rate_")) m[k.slice(8)] = Number(row.value) || 0;
        }
        setHaRates(m); setHaDefault(def);
      }).catch(() => {});
    return () => { alive = false; };
  }, [date, tick]);
  const after = () => { extra.reload(); setTick((t) => t + 1); };

  const none = !extra.items.length && !halladmins.length;
  return (
    <section className={css.dwBlock}>
      <div className={css.detH}>Допработы и админства <button type="button" className={css.dwPeriod} onClick={() => setReview(true)}>за период ›</button></div>
      {none ? <div className={css.dwNone}>сегодня нет</div> : null}{haInfo ? <div className={css.dwNone} role="status">{haInfo}</div> : null}
      {extra.items.map((e) => {
        /* 12.09 (владелец): заявка оператора и назначенная работа выглядели одинаково,
           а платится только назначенная — заявки висели с ₽0 без объяснения. Подписываем
           статус прямо в строке, иначе ноль читается как ошибка расчёта. */
        /* 26.09 (владелец): учёбка ученику не оплачивается — ни ставки, ни суммы, ни
           «принять и оплатить». Сервер такие правки и так отклонит; здесь их просто нет. */
        const study = e.kind === "training";
        const mark = study ? "учёбка · без оплаты"
          : e.status === "proposed" ? "заявка · ждёт решения"
          : e.status === "rework" ? "на доработке у оператора"
          : e.status === "rejected" ? "отклонена" : e.factFrom && e.factTo && !e.factOk && (e.factFrom !== e.timeFrom || e.factTo !== e.timeTo) ? `по отчёту ${e.factFrom}–${e.factTo} · ждёт ОО, платим план`
          : null;
        const paid = sal.ew.get(ewPayKey(e.operatorId, e.id));
        /* Заявку зарплата не считает — но заданную сумму показываем, иначе ввод выглядит
           как «ничего не выставилось» (владелец, 12.09). */
        const shown = paid ?? (e.rate_override ?? 0);
        return (
          <div key={`e${e.id}`}>
          <div className={`${css.dwRow} ${mark ? css.dwPend : ""} ${e.accepted ? css.dwAcc : ""}`} {...hold}>
            <button type="button" className={css.dwIcoBtn} title="Подробности и действия"
              onClick={() => setOpenEw((o) => (o === e.id ? null : e.id))}>🔧</button>
            <span className={css.dwNm} title={e.accepted ? "Принял в Telegram" : "Ещё не принял"}>{e.accepted ? "✓ " : ""}{e.operatorName}</span>{/* 14.10 (владелец): «куратор нажал „принять“ — подсвечивать зелёным», как на доске */}
            <span className={css.dwWhat}>
              <button type="button" className={css.dwTime} title="Итоговое отработанное время — тап изменить"
                onClick={() => setTimePick({
                  label: `Отработал — ${e.operatorName}`, value: `${e.timeFrom}–${e.timeTo}`,
                  save: async (from, to) => { await api(`/api/extra-work/${e.id}`, { method: "PUT", body: { time_from: from, time_to: to } }); after(); },
                })}>{e.timeFrom}–{e.timeTo}</button>
              {e.description ? ` · ${e.description}` : ""}
              {mark ? <span className={css.dwMark}> · {mark}</span> : null}
              {e.draft ? <span className={css.dwMark}> · не отправлено</span> : null}
            </span>
            {/* 12.09: правка ставки сбрасывает вручную выставленный итог (так задумано —
                считаем заново по часам), но об этом надо предупредить: иначе выглядит как
                «сумма сама пропала». */}
            {study ? null : (<>
            <button type="button" className={css.dwRateH}
              title={e.rate_override != null ? "Ставка ₽/ч — тап изменить. Заданная вручную сумма сбросится, итог посчитаем по часам" : "Ставка ₽/ч — тап изменить"}
              onClick={(ev) => setPad({ anchor: ev.currentTarget,
                label: e.rate_override != null ? `Ставка ₽/ч · ${e.operatorName} · итог пересчитаем` : `Ставка ₽/ч · ${e.operatorName}`,
                value: e.hourlyRate,
                save: async (v) => { await api(`/api/extra-work/${e.id}`, { method: "PUT", body: { hourly_rate: v, rate_override: null } }); after(); } })}>
              ₽{fmt(e.hourlyRate)}/ч
            </button>
            <button type="button" className={css.dwSum} title="Итог за работу — тап задать вручную"
              onClick={(ev) => setPad({ anchor: ev.currentTarget, label: `Итого за допработу · ${e.operatorName}`, value: shown,
                save: async (v) => { await api(`/api/extra-work/${e.id}`, { method: "PUT", body: { rate_override: v } }); after(); } })}>
              ₽{fmt(shown)}
            </button>
            </>)}
          </div>
          {openEw === e.id ? (
            <div className={css.dwOpen}>
              <div className={css.dwOpenText}>{e.description || "без описания"}</div>
              <div className={css.dwOpenMeta}>
                {e.operatorName} · {e.timeFrom}–{e.timeTo}{study ? "" : ` · ₽${fmt(e.hourlyRate)}/ч`}
                {e.rate_override != null ? ` · сумма задана вручную: ₽${fmt(e.rate_override)}` : ""}
                {mark ? ` · ${mark}` : ""}
                {paid == null && !study ? " · в зарплату не идёт, пока заявка не принята" : ""}
              </div>
              <div className={css.dwOpenActs}>
                {/* 12.09 (владелец): «если дело в статусе — сделай третью кнопку "принять"».
                    Заявку, возврат и отказ деньги не видят; принятая работа оплачивается
                    как назначенная, вместе с уже выставленной суммой. */}
                {mark && !study ? (
                  <button type="button" className={`${css.dwAct} ${css.dwActOk}`}
                    onClick={() => void api(`/api/extra-work/${e.id}`, { method: "PUT", body: { status: "confirmed" } }).then(() => { setOpenEw(null); after(); }).catch(() => {})}>
                    ✓ Принять и оплатить
                  </button>
                ) : null}
                {study ? null : (
                <button type="button" className={css.dwAct}
                  onClick={() => void api(`/api/extra-work/${e.id}`, { method: "PUT", body: { status: "rework" } }).then(() => { setOpenEw(null); after(); }).catch(() => {})}>
                  ↩ Вернуть оператору
                </button>)}
                <button type="button" className={`${css.dwAct} ${css.dwActDel} ${delArm === e.id ? css.dwActArm : ""}`}
                  onClick={() => {
                    if (delArm !== e.id) { setDelArm(e.id); window.setTimeout(() => setDelArm((d) => (d === e.id ? null : d)), 3000); return; }
                    void api(`/api/extra-work/${e.id}`, { method: "DELETE" }).then(() => { setDelArm(null); setOpenEw(null); after(); }).catch(() => {});
                  }}>
                  {delArm === e.id ? "Точно удалить?" : "✕ Удалить"}
                </button>
              </div>
            </div>
          ) : null}
          </div>
        );
      })}
      {halladmins.map((h, i) => {
        const key = `${h.operatorId}_${h.branch}`;
        const info = sal.ha.get(key);
        const rate = info?.rate || haRates[h.operatorId] || haDefault;   // 13.10: ставка самой смены (из зарплаты), не текущая настройка
        const earning = info?.earning ?? 0;
        return (
          <div key={`h${h.operatorId}_${h.branch}`} className={`${css.dwRow} ${css.dwHa}`} {...hold}>
            <HallAdminTap row={h} rows={halladmins} date={d} onDone={(rows, info, changed) => { if (changed) { onHallAdmins?.(rows); if (rows) after(); } setHaInfo(info); window.clearTimeout(haTimer.current); haTimer.current = window.setTimeout(() => setHaInfo(null), /НЕ доставлено|Не получилось|Не удалось/.test(info) ? 15000 : 9000); }} />
            <span className={css.dwWhat}>
              адм {h.branch.toUpperCase()} ·{" "}
              <button type="button" className={css.dwTime} title="Итоговое отработанное время — тап изменить"
                onClick={() => setTimePick({
                  label: `Админство — ${h.operator}`, value: h.timeFrom && h.timeTo ? `${h.timeFrom}–${h.timeTo}` : "",
                  save: async (from, to) => {
                    await api("/api/hall-admin-reports", { method: "POST", body: { operator_id: h.operatorId, branch_id: h.branch, date: d, time_from: from, time_to: to } });
                    after();
                  },
                })}>{h.timeFrom && h.timeTo ? `${h.timeFrom}–${h.timeTo}` : "задать время"}</button>
              {info?.hours ? ` · ${info.hours} ч` : ""}
            </span>
            <button type="button" className={css.dwRateH} title="Ставка ₽/ч (персональная) — тап изменить"
              onClick={(ev) => h.operatorId && setPad({ anchor: ev.currentTarget, label: `Ставка ₽/ч · ${h.operator}`, value: rate,
                save: async (v) => { await api("/api/finance/ha-rate", { method: "PUT", body: { staff_id: h.operatorId, rate: v } }); after(); } })}>
              ₽{fmt(rate)}/ч
            </button>
            <button type="button" className={css.dwSum} title="Итог за смену — тап задать вручную"
              onClick={(ev) => h.operatorId && setPad({ anchor: ev.currentTarget, label: `Итого за смену · ${h.operator}`, value: earning,
                save: async (v) => { await api("/api/finance/ha-pay", { method: "PUT", body: { staff_id: h.operatorId, date: d, branch_id: h.branch, amount: v } }); after(); } })}>
              ₽{fmt(earning)}
            </button>
          </div>
        );
      })}
      <NumPadPop
        open={!!pad} anchor={pad?.anchor ?? null} label={pad?.label ?? ""}
        mode="money" value={pad?.value ?? 0} max={99999}
        onCommit={(v) => { const sv = pad?.save; if (sv) void sv(v); }} onClose={() => setPad(null)}
        extraKeys={[{ label: "Записать", onPress: (v) => { const sv = pad?.save; setPad(null); if (sv) void sv(v); } }]}
      />
      {timePick ? (
        <TimePicker open mode="range" step={5} label={timePick.label}
          value={timePick.value || undefined}
          onCommit={(v) => {
            const m = v.match(/^(\d{2}:\d{2})[–-](\d{2}:\d{2})$/);
            const sv = timePick.save;
            setTimePick(null);
            if (m) void sv(m[1]!, m[2]!);
          }}
          onCancel={() => setTimePick(null)} />
      ) : null}
      {review ? <ExtraReview from={fmtDateApi(addDays(date, -6))} to={d} onClose={() => { setReview(false); after(); }} /> : null}
    </section>
  );
}

/* ── C. Finance block (27.08, владелец: единственное представление денег,
      сразу под шапкой карточки) — «Услуги | Оплата», цены кликабельны ─────── */
function FinBlock({
  game, primary, onSavePrices, onNoTea, onPersisted,
}: {
  game: OoGameSummary;
  primary: OoReport;
  onSavePrices: (reportId: number, patch: PricesPatch) => Promise<void>;
  /** «Чайной не было» — снять признак у брони, чтобы расхождение закрылось без ложной цены. */
  onNoTea: (bookingId: number) => Promise<void> | void;
  onPersisted: () => void;
}) {
  // Local optimistic view of editable amounts (recompute Δ instantly on save).
  const [vals, setVals] = useState({
    quest_price: primary.questPrice,
    tea_price: primary.teaPrice,
    mc_price: primary.mcPrice,
    night_surcharge: primary.nightSurcharge,
  });
  useEffect(() => {
    setVals({
      quest_price: primary.questPrice, tea_price: primary.teaPrice,
      mc_price: primary.mcPrice, night_surcharge: primary.nightSurcharge,
    });
  }, [primary.questPrice, primary.teaPrice, primary.mcPrice, primary.nightSurcharge]);

  const expectedLocal = vals.quest_price + vals.tea_price + vals.mc_price + vals.night_surcharge;
  const factLocal = primary.cash + primary.terminal + primary.transfer + primary.prepaidUsed + primary.invoice - primary.changeAmount;
  const localDiff = factLocal - expectedLocal;
  const localDisc = Math.abs(localDiff) >= 1;

  const persist = useCallback(async (field: keyof typeof vals, value: number) => {
    setVals((v) => ({ ...v, [field]: value }));
    await onSavePrices(primary.id, { [field]: value });
    onPersisted();
  }, [onSavePrices, primary.id, onPersisted]);

  /* 12.09: «чайной не было» гасим на месте — без перезагрузки ленты. */
  const [teaOff, setTeaOff] = useState(false);
  const teaZeroBanner = !game.isLearning && game.tea && !teaOff && vals.tea_price === 0 && !primary.teaGift;   // 13.10: учебная бесплатна — «подставить чайную» не предлагаем
  /* 26.09 (владелец, скрин 19.09 «Каль»: «добавлены мастер-классы уже после игры, а
     в расчёте их нет»). «Ожидалось» бралось из СДАННОГО отчёта: услуги добавили в
     бронь позже, и в отчёте навсегда остался 0 — ожидалось 7 200 при факте 18 000.
     Считаем цену МК по брони той же формулой, что форма отчёта (mcClientTotal,
     @ub/shared), по фактическим игрокам. Автоматом в отчёт не пишем — по образцу
     чайной показываем расхождение и даём подставить одним касанием: это деньги,
     и ОО видит, что меняется. На зарплату операторов не влияет — их ставки за МК
     идут из mc_booking_ops, mc_price отчёта в расчёте зарплаты не участвует. */
  const mcPlayers = primary.actualPlayers ?? game.players;
  const mcExpected = game.isLearning ? 0 : mcClientTotal(game.mcPrices, mcPlayers);
  const mcNames = game.mcPrices.map((m) => m.mcName).join(", ");
  const mcZeroBanner = mcExpected > 0 && vals.mc_price === 0;
  // Default tea price unknown client-side; fall back to a sane sheet value.
  const TEA_DEFAULT = 2000;

  return (
    <>
      {teaZeroBanner ? (
        <div className={css.teaBanner}>
          <IcCup />
          <span className={css.tbTxt}>По брони чайная, в отчёте ₽0 — оператор сдал без чайной. Подставить цену из справочника?</span>
          <button type="button" className={css.tbBtn} onClick={() => persist("tea_price", TEA_DEFAULT)}>Подставить</button>{/* 01.10: была, но бесплатно */}<button type="button" className={`${css.tbBtn} ${css.tbBtnGhost}`} onClick={() => { void onSavePrices(primary.id, { tea_gift: 1 }).then(onPersisted); }}>Чайная в подарок</button>
          {/* 11.09 (владелец): расхождение бывает и в другую сторону — чайную записали в бронь,
              а её не было. Тогда снимаем признак у брони, и плашка уходит без ложной цены. */}
          <button type="button" className={`${css.tbBtn} ${css.tbBtnGhost}`}
            onClick={() => { setTeaOff(true); void onNoTea(game.bookingId); }}>Чайной не было</button>
        </div>
      ) : null}
      {mcZeroBanner ? (
        <div className={css.teaBanner}>
          <IcStar />
          <span className={css.tbTxt}>
            По брони мастер-классы ({mcNames}) на {fmt(mcExpected)}₽, в отчёте ₽0 — их добавили
            после сдачи отчёта. Подставить цену из справочника?
          </span>
          <button type="button" className={css.tbBtn} onClick={() => persist("mc_price", mcExpected)}>Подставить</button>
        </div>
      ) : null}

      <div className={css.twoCol}>
        <div className={css.col}>
          <div className={css.colH}>Услуги</div>
          <EditRow icon={<IcStar />} label="Квест" value={vals.quest_price} dflt={primary.questPrice} onSave={(v) => persist("quest_price", v)} />
          {(game.tea && !teaOff) || vals.tea_price > 0 ? (
            <EditRow icon={<IcCup />} label={primary.teaGift ? "Чайная · подарок" : "Чайная"} value={vals.tea_price} dflt={primary.teaPrice} onSave={(v) => persist("tea_price", v)} />
          ) : null}
          {vals.mc_price > 0 || mcExpected > 0 ? (
            <EditRow icon={<IcStar />} label="МК" value={vals.mc_price} dflt={mcExpected || primary.mcPrice} onSave={(v) => persist("mc_price", v)} />
          ) : null}
          {vals.night_surcharge > 0 ? (
            <EditRow icon={<IcChev />} label="Ночной" value={vals.night_surcharge} dflt={primary.nightSurcharge} onSave={(v) => persist("night_surcharge", v)} />
          ) : null}
          {/* 05.09 (владелец): такси — расход оператора, не услуга клиенту; из «Услуг» убрано, живёт в чипе оператора */}
          <div className={`${css.lrow} ${css.lrowTotal}`}>
            <span className={css.lk}><span className={css.lkT}>Ожидалось</span></span>
            <span className={css.totv}>{fmt(expectedLocal)}₽</span>
          </div>
        </div>
        <div className={`${css.col} ${css.colPay}`}>
          <div className={css.colH}>Оплата</div>
          {primary.cash > 0 ? <ReadRow icon={<IcCash />} label="Нал" value={primary.cash} /> : null}
          {primary.terminal > 0 ? <ReadRow icon={<IcCard />} label="Терминал" value={primary.terminal} /> : null}
          {primary.transfer > 0 ? <ReadRow icon={<IcTransfer />} label="Перевод" value={primary.transfer} /> : null}
          {primary.prepaidUsed > 0 ? <ReadRow icon={<IcWallet />} label="ЗДТК" value={primary.prepaidUsed} /> : null}
          {/* 28.08 (владелец): задаток БРОНИ красным, если оператор его не зачёл.
              v2: строка как все (не вылезает), пояснение — отдельной мелкой. */}
          {game.bookingPrepaid > 0 && primary.prepaidUsed !== game.bookingPrepaid ? (
            <div className={css.prepaidWarn}>
              <div className={css.lrow}>
                <span className={css.lk}><IcWallet /><span className={css.lkT}>ЗДТК брони</span></span>
                <span className={css.editv}>{fmt(game.bookingPrepaid)}₽</span>
              </div>
              <div className={css.prepaidNote}>
                {primary.prepaidUsed > 0 ? `в отчёте зачтено ₽${fmt(primary.prepaidUsed)}` : "в отчёте не зачтён"}
              </div>
            </div>
          ) : null}
          {primary.invoice > 0 ? <ReadRow icon={<IcDoc />} label="Р/С" value={primary.invoice} /> : null}
          {primary.changeAmount > 0 ? <ReadRow icon={<IcReturn />} label="Сдача" value={primary.changeAmount} /> : null}
          <div className={`${css.lrow} ${css.lrowTotal}`}>
            <span className={css.lk}><span className={css.lkT}>Факт</span></span>
            <span className={`${css.totv} ${localDisc ? css.totvBad : ""}`}>{fmt(factLocal)}₽</span>
          </div>
        </div>
      </div>
    </>
  );
}

/* ── C. Inline expansion ──────────────────────────────────────────────────── */
function Expanded({
  game, primary, st, onAccept, onReturn, onArchive, onSavePrices, fetchEdits, editsKey,
}: {
  game: OoGameSummary;
  primary: OoReport;
  st: GameStatus;
  onAccept: () => void;
  onReturn: () => void;
  onArchive: () => void;
  onSavePrices: (reportId: number, patch: PricesPatch) => Promise<void>;
  fetchEdits: (bookingId: number) => Promise<OoReportEdit[]>;
  /** bump из FinBlock → перечитать историю правок */
  editsKey: number;
}) {
  const { hasDisc, accepted } = st;
  const [ooComment, setOoComment] = useState(primary.ooComment);
  const [edits, setEdits] = useState<OoReportEdit[] | null>(null);
  const [editsLoading, setEditsLoading] = useState(true);
  const [histOpen, setHistOpen] = useState(false);

  const loadEdits = useCallback(() => {
    setEditsLoading(true);
    fetchEdits(game.bookingId).then((e) => { setEdits(e); setEditsLoading(false); });
  }, [fetchEdits, game.bookingId]);
  useEffect(() => { loadEdits(); }, [loadEdits, editsKey]);

  const saveOoComment = useCallback(async () => {
    if (ooComment === primary.ooComment) return;
    await onSavePrices(primary.id, { oo_comment: ooComment });
  }, [ooComment, primary.ooComment, primary.id, onSavePrices]);

  return (
    <div className={css.detail}>
      {/* «Состав» растворён в чипах шапки (27.08, владелец): рамка = сдача,
          жирная — первичный, ставка и такси правятся из чипа */}

      {/* Игроки из отчёта оператора + табличка вейвер-данных (27.08, владелец) */}
      {game.reportPlayers.length > 0 ? (
        <div className={css.detPeers}>
          <div className={css.detH}>Игроки ({game.reportPlayers.length})</div>
          {game.reportPlayers.map((p, i) => (
            <div key={`rp${i}`} className={css.plRow}>
              <span className={css.peerIco}>{p.captain ? <IcStar /> : <IcPerson />}</span>
              <span className={css.plName}>{p.name}</span>
              {p.birthDate ? <span className={css.plAge}>{ageFrom(p.birthDate)} лет</span> : null}
              {p.phone ? <span className={css.plPhone}>{p.phone}</span> : null}
              {p.tags.map((t) => <span key={t} className={css.plTag}>{t}</span>)}
            </div>
          ))}
        </div>
      ) : null}
      {game.waiverPersons.length > 0 || game.waiverPhotos.length > 0 || game.noWaiver ? (
        <div className={css.detPeers}>
          <div className={css.detH}>Вейвер{game.waiverPersons.length ? ` (${game.waiverPersons.length})` : ""}</div>
          {game.waiverPersons.map((w, i) => (
            <div key={`wp${i}`} className={css.plRow}>
              <span className={css.peerIco}><IcDoc /></span>
              <span className={css.plName}>{w.fio}</span>
              {w.birthDate ? <span className={css.plAge}>{ageFrom(w.birthDate)} лет</span> : null}
              {w.phone ? <span className={css.plPhone}>{w.phone}</span> : null}
            </div>
          ))}
          {game.noWaiver && !game.waiverPhotos.length ? <div className={css.plRow}><span className={css.peerIco}><IcDoc /></span><span className={css.plName}>Вейвера нет</span><span className={css.plTag}>{game.noWaiver}</span></div> : null}
          {game.waiverPhotos.length > 0 ? (
            <div className={css.wvGrid}>
              {game.waiverPhotos.map((p) => <WvShot key={p.id} photoId={p.id} by={p.by} />)}
            </div>
          ) : null}
        </div>
      ) : null}
      {/* Отчёты друг о друге (27.08 — сразу, целиком; 13.10 — блок в PeerTextsBlock, виден и до основного отчёта) */}<PeerTextsBlock game={game} person={<IcPerson />} arrow={<IcArrow />} />

      {/* OO comment */}
      <div className={css.ooComment}>
        <div className={css.detH}>Комментарий OO</div>
        <textarea
          value={ooComment}
          placeholder="Заметка для отчёта…"
          onChange={(e) => setOoComment(e.target.value)}
          onBlur={saveOoComment}
        />
      </div>

      {/* Edit history — свёрнута по умолчанию (27.08, владелец) */}
      <div className={css.editHist}>
        <button type="button" className={`${css.detH} ${css.detHBtn}`} onClick={() => setHistOpen((v) => !v)}>
          <span className={css.detHChev}>{histOpen ? "▾" : "›"}</span> История правок{edits && edits.length ? ` (${edits.length})` : ""}
        </button>
        {!histOpen ? null : editsLoading ? (
          <div className={css.ehEmpty}>Загрузка истории…</div>
        ) : edits && edits.length ? (
          edits.map((e) => (
            <div key={e.id} className={css.ehRow}>
              <div className={css.ehMeta}>
                <span className={css.ehT}>{e.createdAt.slice(11, 16)}</span>
                <span className={css.ehWho}>{e.editorName || "—"}</span>
              </div>
              <div className={css.ehChange}>
                <span className={css.ehF}>{editFieldLabel(e.field)}</span>
                <span className={css.ehOld}>{e.oldValue || "—"}</span>
                <span className={css.ehArr}>→</span>
                <span className={css.ehNew}>{e.newValue || "—"}</span>
              </div>
            </div>
          ))
        ) : (
          <div className={css.ehEmpty}>Правок нет</div>
        )}
      </div>

      {/* Footer actions */}
      <div className={css.detFooter}>
        {hasDisc && !accepted ? (
          <button type="button" className={`${css.dfBtn} ${css.dfAccept}`} onClick={onAccept}><IcCheck />Принять</button>
        ) : null}
        <button type="button" className={`${css.dfBtn} ${css.dfReturn}`} onClick={onReturn}><IcReturn />Вернуть</button>
        <button type="button" className={`${css.dfBtn} ${css.dfComment}`} onClick={saveOoComment}><IcComment />Коммент OO</button>
      </div>
    </div>
  );
}

/* ── C. Read-only amount row ──────────────────────────────────────────────── */
function ReadRow({ icon, label, value }: { icon: ReactElement; label: string; value: number }) {
  return (
    <div className={css.lrow}>
      <span className={css.lk}>{icon}<span className={css.lkT}>{label}</span></span>
      <span className={css.editv}>{fmt(value)}₽</span>
    </div>
  );
}

/* ── C. Click-to-edit amount row (autosave → flash → recompute) ───────────── */
function EditRow({
  icon, label, value, dflt, onSave,
}: {
  icon: ReactElement;
  label: string;
  value: number;
  dflt: number;
  onSave: (value: number) => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const [flash, setFlash] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  const commit = useCallback(() => {
    const next = Math.max(0, Math.round(Number(draft.replace(/[^\d.-]/g, "")) || 0));
    setEditing(false);
    if (next !== value) {
      void Promise.resolve(onSave(next)).then(() => {
        setFlash(true);
        setTimeout(() => setFlash(false), 700);
      });
    }
  }, [draft, value, onSave]);

  const overridden = Math.round(value) !== Math.round(dflt);

  return (
    <div className={css.lrow}>
      <span className={css.lk}>{icon}<span className={css.lkT}>{label}</span></span>
      {editing ? (
        <input
          ref={inputRef}
          className={css.editinp}
          inputMode="numeric"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            else if (e.key === "Escape") { setDraft(String(value)); setEditing(false); }
          }}
        />
      ) : (
        <button
          type="button"
          className={`${css.editv} ${overridden ? css.overridden : ""} ${flash ? css.flash : ""}`}
          onClick={() => { setDraft(String(value)); setEditing(true); }}
        >
          {fmt(value)}₽
        </button>
      )}
    </div>
  );
}

/* ── D. Return-reason bottom sheet ────────────────────────────────────────── */
/* 27.08 (владелец): пресеты причин — личные для каждого ОО (localStorage,
   правка карандашом в самом шите); возврат — выбранным операторам, не всем. */
const LS_RETURN_PRESETS = "ub_return_presets";
function loadReturnPresets(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(LS_RETURN_PRESETS) || "");
    if (Array.isArray(v) && v.length && v.every((x) => typeof x === "string")) return v;
  } catch { /* нет сохранённых / битые — дефолт */ }
  return RETURN_PRESETS;
}

function ReturnSheet({
  game, onClose, onSubmit,
}: {
  game: OoGameSummary;
  onClose: () => void;
  onSubmit: (reason: string, operatorIds: string[]) => void | Promise<void>;
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [presets, setPresets] = useState<string[]>(loadReturnPresets);
  const [addOpen, setAddOpen] = useState(false);
  const lp = useRef(0); const lpFired = useRef(false);

  const savePresets = useCallback((next: string[]) => {
    setPresets(next);
    try { localStorage.setItem(LS_RETURN_PRESETS, JSON.stringify(next)); } catch { /* private mode */ }
  }, []);
  const dropPreset = useCallback((p: string) => {
    savePresets(presets.filter((x) => x !== p));
    setPicked((s) => { const n = new Set(s); n.delete(p); return n; });
  }, [presets, savePresets]);

  const reason = useMemo(() => {
    const presets2 = [...picked].join(", ");
    return [presets2, text.trim()].filter(Boolean).join(". ").trim();
  }, [picked, text]);

  // Авторы отчётов игры; изначально НИКТО не выбран (27.08, владелец) — ОО
  // явно выбирает, кому вернуть.
  const authors = useMemo(() => {
    const seen = new Set<string>();
    return game.reports
      .filter((r) => r.operatorId && !seen.has(r.operatorId) && seen.add(r.operatorId))
      .map((r) => ({ id: r.operatorId, name: r.operatorName }));
  }, [game.reports]);
  const [selOps, setSelOps] = useState<Set<string>>(() => new Set());

  const submit = useCallback(async () => {
    if (!reason || busy || selOps.size === 0) return;
    setBusy(true);
    try { await onSubmit(reason, [...selOps]); } finally { setBusy(false); }
  }, [reason, busy, onSubmit, selOps]);

  return (
    <div className={css.sheetHost} role="dialog" aria-modal="true">
      <div className={css.sheetBackdrop} onClick={onClose} />
      <div className={css.sheetPanel}>
        <div className={css.sheetHandle} />
        <div className={css.sheetHead}>
          <span className={css.sheetTitle}>Причина возврата</span>
          <button type="button" className={css.sheetClose} aria-label="Закрыть" onClick={onClose}><IcX /></button>
        </div>
        <div className={css.sheetBody}>
          {authors.length > 0 ? (
            <>
              <div className={css.sbSub}>Кому вернуть</div>
              <div className={css.presetRow}>
                {authors.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={`${css.preset} ${selOps.has(a.id) ? css.on : ""}`}
                    onClick={() => setSelOps((s) => { const n = new Set(s); if (n.has(a.id)) n.delete(a.id); else n.add(a.id); return n; })}
                  >
                    {a.name}
                  </button>
                ))}
              </div>
            </>
          ) : null}
          {/* 28.08 (владелец): проще — «+ своя» всегда на месте (TextPad),
              удалить пресет = долгий тап по чипу */}
          <div className={css.sbSub}>Быстрые причины <i className={css.sbHint}>долгий тап — удалить</i></div>
          <div className={css.presetRow}>
            {presets.map((p) => (
              <button
                key={p}
                type="button"
                className={`${css.preset} ${picked.has(p) ? css.on : ""}`}
                onPointerDown={() => { lpFired.current = false; lp.current = window.setTimeout(() => { lpFired.current = true; dropPreset(p); }, 450); }}
                onPointerUp={() => window.clearTimeout(lp.current)} onPointerLeave={() => window.clearTimeout(lp.current)}
                onContextMenu={(e) => e.preventDefault()}
                onClick={() => { if (lpFired.current) { lpFired.current = false; return; } setPicked((s) => { const n = new Set(s); if (n.has(p)) n.delete(p); else n.add(p); return n; }); }}
              >
                {p}
              </button>
            ))}
            <button type="button" className={`${css.preset} ${css.presetNew}`} onClick={() => setAddOpen(true)}>+ своя</button>
          </div>
          <TextPad open={addOpen} label="Своя причина (сохранится в твои пресеты)" placeholder="Причина…" maxLength={80}
            onCommit={(v) => { const t = v.trim(); if (t) { savePresets(presets.includes(t) ? presets : [...presets, t]); setPicked((s) => new Set(s).add(t)); } }} onClose={() => setAddOpen(false)} />
          <div className={css.sbSub}>Комментарий</div>
          <textarea value={text} placeholder="Опишите, что исправить…" onChange={(e) => setText(e.target.value)} />
        </div>
        <div className={css.sheetFoot}>
          <button type="button" className={css.sheetSubmit} disabled={!reason || busy || selOps.size === 0} onClick={submit}>
            {selOps.size > 0 && selOps.size < authors.length
              ? `Вернуть: ${authors.filter((a) => selOps.has(a.id)).map((a) => a.name).join(", ")}`
              : "Отправить на коррекцию"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── D/E. Custom confirm modal (double-confirm for archive) ───────────────── */
function ConfirmModal({
  kind, title, body, confirmLabel, singleStep, onCancel, onConfirm,
}: {
  kind: "archive" | "accept";
  title: string;
  body: ReactElement;
  confirmLabel: string;
  singleStep?: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const danger = kind === "archive";

  const go = useCallback(async () => {
    if (!singleStep && danger && step === 1) { setStep(2); return; }
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  }, [singleStep, danger, step, onConfirm]);

  return (
    <div className={css.modalHost} role="dialog" aria-modal="true">
      <div className={css.modalBackdrop} onClick={onCancel} />
      <div className={`${css.modal} ${danger ? "" : css.modalAccept}`}>
        <div className={css.mTitle}>{title}</div>
        <div className={css.mBody}>
          {!singleStep && danger && step === 2 ? <>Точно в архив? Это действие нельзя отменить из ленты.</> : body}
        </div>
        <div className={css.mActions}>
          <button type="button" className={`${css.mBtn} ${css.mCancel}`} onClick={onCancel}>Отмена</button>
          <button type="button" className={`${css.mBtn} ${danger ? css.mDanger : css.mGo}`} disabled={busy} onClick={go}>
            {!singleStep && danger && step === 1 ? "Далее" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Helpers ──────────────────────────────────────────────────────────────── */
/** Фото вейвера (27.08): защищённый файл → objectURL; тап — во весь экран.
 *  28.08 (владелец): в просмотре — поворот на 90° и обрезка рамкой, «Сохранить»
 *  перезаписывает файл на сервере (PUT /waiver-file/:id). 10.10: правка — общий
 *  usePhotoEdit (тот же, что у медиа игры). */
function WvShot({ photoId, by }: { photoId: number; by: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [full, setFull] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true; let u: string | null = null;
    apiFileUrl(`/api/game-reports/waiver-file/${photoId}`)
      .then((x) => { if (alive) { u = x; setUrl(x); } else URL.revokeObjectURL(x); })
      .catch(() => {});
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [photoId]);
  const ed = usePhotoEdit(url);
  const save = async () => {
    if (!ed.edited || busy) return;
    setBusy(true);
    try {
      const file = await ed.takeFile("waiver-edit.jpg");
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api(`/api/game-reports/waiver-file/${photoId}`, { method: "PUT", body: fd, retries: 0 });
        const saved = ed.commit();
        if (url) URL.revokeObjectURL(url);
        setUrl(saved);
      }
    } catch { /* не сохранилось — правка остаётся локально, можно повторить */ }
    setBusy(false);
  };
  if (!ed.shown) return <span className={css.wvShotStub} />;
  return (
    <>
      <button type="button" className={css.wvShot} title={by ? `прикрепил ${by}` : undefined} onClick={() => setFull(true)}>
        <img src={ed.shown} alt="вейвер" />
      </button>
      {full ? (
        <div className={css.mvOverlay} role="dialog" aria-modal="true" onClick={() => { if (!ed.cropMode) setFull(false); }}>
          <div className={css.wvEditWrap} onClick={(e) => e.stopPropagation()}>
            <span className={css.wvImgWrap} {...ed.pointer}>
              <img ref={ed.imgRef} className={css.wvFull} src={ed.shown} alt="вейвер" draggable={false} />
              {ed.cropMode && ed.sel ? <span className={css.wvSel} style={{ left: ed.sel.x, top: ed.sel.y, width: ed.sel.w, height: ed.sel.h }} /> : null}
            </span>
            <div className={css.wvBar}>
              <button type="button" className={css.wvBtn} onClick={ed.rotate} disabled={ed.cropMode || ed.busy}>⟳ повернуть</button>
              {ed.cropMode
                ? <>
                    <button type="button" className={`${css.wvBtn} ${css.wvOk}`} onClick={ed.applyCrop} disabled={!ed.sel || ed.sel.w < 8}>✂ применить</button>
                    <button type="button" className={css.wvBtn} onClick={ed.cancelCrop}>отмена</button>
                  </>
                : <button type="button" className={css.wvBtn} onClick={ed.startCrop} disabled={ed.busy}>✂ обрезать</button>}
              {ed.edited ? <button type="button" className={`${css.wvBtn} ${css.wvSave}`} onClick={() => void save()} disabled={busy}>{busy ? "…" : "Сохранить"}</button> : null}
              <button type="button" className={css.wvBtn} onClick={() => { setFull(false); ed.cancelCrop(); }}>✕</button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function gamesWord(n: number): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "игра";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "игры";
  return "игр";
}
function discWord(n: number): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "расхождение";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "расхождения";
  return "расхождений";
}
const EDIT_FIELD_LABELS: Record<string, string> = {
  quest_price: "Квест ₽",
  tea_price: "Чайная ₽", tea_gift: "Чайная в подарок",
  mc_price: "МК ₽",
  night_surcharge: "Ночной ₽",
  taxi_amount: "Такси",
  cash: "Нал",
  terminal: "Терминал",
  transfer: "Перевод",
  prepaid_used: "ЗДТК",
  invoice: "Р/С",
  total: "Итого",
  actual_players: "Игроков",
  comment: "Комментарий",
  activity: "Активность",
  change_amount: "Сдача",
};
function editFieldLabel(field: string): string {
  return EDIT_FIELD_LABELS[field] ?? field;
}

/* ── Доп. материалы к любой игре (фото/аудио/видео → клиентский протокол) ───────
 * Тот же эндпоинт, что у операторов (/api/game-reports/:id/material). OO и любой
 * сотрудник может догрузить материалы к игре прямо из дашборда; игроки получают
 * пуш и видят файлы в разделе «Сессии». */
function MaterialsUploader({ bookingId }: { bookingId: number }) {
  const [state, setState] = useState<"idle" | "uploading" | "done" | "fail">("idle");
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [uploaded, setUploaded] = useState<{ url: string; type: string }[]>([]);
  const urlsRef = useRef<string[]>([]);
  useEffect(() => () => { urlsRef.current.forEach((u) => URL.revokeObjectURL(u)); }, []);

  const onPick = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // позволить выбрать тот же файл повторно
    if (!files.length) return;
    setState("uploading"); setTotal(files.length); setDone(0);
    let okAll = true, i = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api(`/api/game-reports/${bookingId}/material`, { method: "POST", body: fd, retries: 0 });
        const url = URL.createObjectURL(f);
        urlsRef.current.push(url);
        setUploaded((prev) => [...prev, { url, type: f.type }]);
      }
      catch { okAll = false; }
      setDone(++i);
    }
    setState(okAll ? "done" : "fail");
  }, [bookingId]);

  /* 28.08 (владелец): «+» живёт внутри блока «Медиа», размер — как миниатюра */
  return (
    <>
      {uploaded.map((u, idx) => (
        <span key={idx} className={css.matNew} title="Загружено">
          {u.type.startsWith("video/") ? <video src={u.url} muted playsInline preload="metadata" />
            : u.type.startsWith("image/") ? <img src={u.url} alt="" />
            : <span className={css.matAudioIco}>♪</span>}
        </span>
      ))}
      <label className={css.matPlus} title="Добавить фото / аудио / видео — уйдёт игрокам этой игры (до 40 МБ на файл)">
        {state === "uploading" ? <span className={css.matPlusSub}>{done}/{total}</span> : "+"}
        <input type="file" accept="image/*,audio/*,video/*" multiple onChange={onPick} disabled={state === "uploading"} hidden />
      </label>
    </>
  );
}


